import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterBar from '@/components/filters/FilterBar';
import TickerFilterBar from '@/components/filters/TickerFilterBar';
import ChannelFilterBar from '@/components/filters/ChannelFilterBar';
import { calculateDateRange } from '@/components/filters/DateRangePreset';
import ViewpointList from '@/components/viewpoints/ViewpointList';
import LoginModal from '@/components/common/LoginModal';
import Disclaimer from '@/components/common/Disclaimer';
import { useViewpoints } from '@/hooks/useViewpoints';
import { useAvailableDates } from '@/hooks/useAvailableDates';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import type { ViewpointFilters, DateRange } from '@/types';

export default function HomePage() {
  const { date: urlDate, channelName: urlChannel } = useParams<{ date?: string; channelName?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const { data: availableDates = [], isLoading: datesLoading } = useAvailableDates();
  const { data: filterOptions, isLoading: optionsLoading } = useFilterOptions();

  // Detect if URL param is a date (8 digits) or ticker
  const isDateParam = urlDate && /^\d{8}$/.test(urlDate);

  // Date is derived from URL (single source of truth)
  const currentDate = useMemo(() => {
    if (isDateParam) {
      return urlDate;
    }
    return null;
  }, [urlDate, isDateParam]);

  // Ticker is derived from URL when not a date
  const currentTicker = useMemo(() => {
    if (urlDate && !isDateParam) {
      return urlDate.toUpperCase();
    }
    return null;
  }, [urlDate, isDateParam]);

  // Channel is derived from URL for blogger page
  const currentChannel = useMemo(() => {
    return urlChannel ? decodeURIComponent(urlChannel) : null;
  }, [urlChannel]);

  // Dynamic page title based on current mode
  const pageTitle = useMemo(() => {
    if (currentChannel) return currentChannel;
    if (currentTicker) return `${currentTicker} 观点汇总`;
    if (currentDate) return `${currentDate} 观点`;
    return undefined; // Use default title for homepage
  }, [currentChannel, currentTicker, currentDate]);

  usePageTitle(pageTitle);

  // Other filters stored in state
  const [otherFilters, setOtherFilters] = useState({
    tickers: [] as string[],
    channels: [] as string[],
    onlyExplicit: false,
  });

  // Ticker page specific filters
  const [tickerDateRange, setTickerDateRange] = useState<DateRange>(() => calculateDateRange('week'));
  const [tickerChannels, setTickerChannels] = useState<string[]>([]);

  // Channel/blogger page specific filters
  const [channelDateRange, setChannelDateRange] = useState<DateRange>(() => calculateDateRange('week'));
  const [channelTickers, setChannelTickers] = useState<string[]>([]);

  // Combined filters for querying (don't include date/ticker/channel if not logged in)
  const filters: ViewpointFilters = useMemo(() => ({
    date: user ? currentDate : null,
    ticker: user ? currentTicker : null,
    channel: user ? currentChannel : null,
    dateRange: (currentTicker || currentChannel) && user
      ? (currentTicker ? tickerDateRange : channelDateRange)
      : null,
    tickers: currentTicker ? [] : (currentChannel ? channelTickers : otherFilters.tickers),
    channels: currentTicker ? tickerChannels : (currentChannel ? [] : otherFilters.channels),
    onlyExplicit: otherFilters.onlyExplicit,
  }), [currentDate, currentTicker, currentChannel, otherFilters, user, tickerDateRange, tickerChannels, channelDateRange, channelTickers]);

  // Handle filter changes - navigate for date, setState for others
  const handleFiltersChange = useCallback((newFilters: ViewpointFilters) => {
    // If selecting a date and user is not logged in, show login modal
    if (newFilters.date && newFilters.date !== currentDate && !user) {
      setLoginModalOpen(true);
      return;
    }

    // If date changed, navigate to new URL
    if (newFilters.date !== currentDate) {
      if (newFilters.date) {
        navigate(`/${newFilters.date}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }

    // Update other filters in state
    setOtherFilters({
      tickers: newFilters.tickers,
      channels: newFilters.channels,
      onlyExplicit: newFilters.onlyExplicit,
    });
  }, [currentDate, navigate, user]);

  const { data: viewpoints = [], isLoading: viewpointsLoading, error } = useViewpoints(filters);

  const isInitialLoading = datesLoading || optionsLoading;

  // Check if user is trying to view a date, ticker, or channel page without being logged in
  const needsAuth = (currentDate || currentTicker || currentChannel) && !user;

  return (
    <Box>
      {/* Header - different for ticker/channel page vs default */}
      {currentTicker ? (
        <Box sx={{ mb: 3 }}>
          <Button
            onClick={() => navigate('/ticker-tracking')}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
          >
            看看别的标的
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {currentTicker} 历史观点
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            查看所有博主对 {currentTicker} 的历史评论
          </Typography>
          <Disclaimer />
        </Box>
      ) : currentChannel ? (
        <Box sx={{ mb: 3 }}>
          <Button
            onClick={() => navigate('/channel-tracking')}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
          >
            看看别的博主
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {currentChannel} 历史观点
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            查看该博主的所有历史评论
          </Typography>
          <Disclaimer />
        </Box>
      ) : (
        <>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            每日 Youtube 美股博主观点笔记
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            根据日期、博主、 ticker 筛选关键观点，轻松查看观点所对应的视频片段
          </Typography>
          <Disclaimer />
        </>
      )}

      {isInitialLoading ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          Loading filters...
        </Box>
      ) : (
        <>
          {/* Show appropriate filter bar based on mode */}
          {currentTicker ? (
            <TickerFilterBar
              dateRange={tickerDateRange}
              channels={tickerChannels}
              availableChannels={filterOptions?.channels || []}
              onDateRangeChange={setTickerDateRange}
              onChannelsChange={setTickerChannels}
            />
          ) : currentChannel ? (
            <ChannelFilterBar
              dateRange={channelDateRange}
              tickers={channelTickers}
              availableTickers={filterOptions?.tickers || []}
              onDateRangeChange={setChannelDateRange}
              onTickersChange={setChannelTickers}
            />
          ) : (
            <FilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              availableDates={availableDates}
              availableTickers={filterOptions?.tickers || []}
              availableChannels={filterOptions?.channels || []}
            />
          )}

          {needsAuth ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {currentTicker
                  ? `请登录以查看 ${currentTicker} 历史观点`
                  : currentChannel
                    ? `请登录以查看 ${currentChannel} 历史观点`
                    : '请登录以查看当日博主观点'}
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setLoginModalOpen(true)}
              >
                点击登录
              </Typography>
            </Box>
          ) : currentDate || currentTicker || currentChannel ? (
            <ViewpointList
              viewpoints={viewpoints}
              isLoading={viewpointsLoading}
              error={error}
              mode={currentTicker || currentChannel ? 'ticker' : 'date'}
              ticker={currentTicker || undefined}
              channel={currentChannel || undefined}
              dateRangePreset={currentTicker ? tickerDateRange.preset : channelDateRange.preset}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                请在上方日历中选择一个日期
              </Typography>
              <Typography variant="body2" color="text.secondary">
                选择日期后即可查看当日博主观点
              </Typography>
            </Box>
          )}
        </>
      )}

      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </Box>
  );
}
